import React from "react";
import { FiCheck } from "react-icons/fi";
import "./OnboardingProgressBar.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

interface Props {
  currentStep: number;
  totalSteps?: number;
  /** Talent sign-up with its Connect Instagram step (build 54): profile, Instagram, bank. */
  withInstagram?: boolean;
}

const INSTAGRAM_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17" cy="7" r="0.6" fill="currentColor" />
  </svg>
);

// Icons per visible onboarding step (1-indexed). Mapped to the user-visible
// step numbers shown above the bar, not the internal Step1Welcome…Step6Complete
// component indices. See OnboardingPage.tsx for the visible↔internal mapping.
//   1 = Profile (photo, name, Instagram)  → user/DJ icon
//   2 = Complete (connect bank)           → bank icon
// With the Instagram step (build 54): 1 profile, 2 Instagram, 3 bank.
const STEP_ICONS: Record<number, React.ReactNode> = {
  1: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  2: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 21h18" />
      <path d="M3 10h18" />
      <path d="M5 6l7-3 7 3" />
      <path d="M4 10v11" />
      <path d="M20 10v11" />
      <path d="M8 14v4" />
      <path d="M12 14v4" />
      <path d="M16 14v4" />
    </svg>
  ),
};

export default function OnboardingProgressBar({ currentStep, totalSteps = 4, withInstagram = false }: Props) {
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
  const iconOf = (n: number) => (withInstagram ? [STEP_ICONS[1], INSTAGRAM_ICON, STEP_ICONS[2]][n - 1] : STEP_ICONS[n]);

  return (
    <div className="onboarding-progress-wrap">
      <p className="onboarding-progress-label">
        <T k="onboardingProgressBar.stepOf" vars={{ currentStep, totalSteps }} tags={{ 0: (c) => <span className="onboarding-progress-current">{c}</span> }} />
      </p>
      <div className="onboarding-progress" aria-label={t("onboardingProgressBar.progress")}>
        {steps.map((stepNum, index) => {
          const isDone = stepNum < currentStep;
          const isActive = stepNum === currentStep;
          const icon = iconOf(stepNum);

          return (
            <React.Fragment key={stepNum}>
              <div
                className={`op-step ${isDone ? "op-step--done" : ""} ${
                  isActive ? "op-step--active" : ""
                }`}
              >
                <span className="op-dot">
                  {icon}
                  {isDone && (
                    <span className="op-check-badge" aria-hidden>
                      <FiCheck />
                    </span>
                  )}
                </span>
              </div>
              {index < totalSteps - 1 && (
                <div
                  className={`op-line ${isDone ? "op-line--done" : ""}`}
                  aria-hidden
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
